import type { Session, SessionReader } from '@/api/client';

function sessionUpdatedAtMs(session: Pick<Session, 'updated_at'>): number {
  return session.updated_at ? new Date(session.updated_at).getTime() : 0;
}

function readersProgressSignature(readers: SessionReader[] | undefined): string {
  if (!readers || readers.length === 0) return '';
  return readers
    .map((reader) => `${reader.id}:${reader.progress ?? 0}`)
    .sort()
    .join('|');
}

/** Denormalized project+reader progress mirrored onto session payloads. */
export function hasSessionProgressFieldsChanged(
  cached: Pick<Session, 'reader_progress' | 'readers'>,
  incoming: Pick<Session, 'reader_progress' | 'readers'>
): boolean {
  if ((cached.reader_progress ?? 0) !== (incoming.reader_progress ?? 0)) return true;
  return readersProgressSignature(cached.readers) !== readersProgressSignature(incoming.readers);
}

export function mergeSessionProgressFields(cached: Session, incoming: Session): Session {
  return {
    ...cached,
    reader_progress: incoming.reader_progress,
    readers: incoming.readers ?? cached.readers,
  };
}

function sessionFieldKey(v: unknown): string {
  if (v == null) return '';
  return String(v);
}

/** Поля, которые меняют геометрию/содержимое пузырька, кроме live-progress. */
export function sessionScheduleFieldsEqual(a: Session, b: Session): boolean {
  if (sessionFieldKey(a.date) !== sessionFieldKey(b.date)) return false;
  if (sessionFieldKey(a.start) !== sessionFieldKey(b.start)) return false;
  if (sessionFieldKey(a.end) !== sessionFieldKey(b.end)) return false;
  if (sessionFieldKey(a.studio) !== sessionFieldKey(b.studio)) return false;
  if (sessionFieldKey(a.project_id) !== sessionFieldKey(b.project_id)) return false;
  if (sessionFieldKey(a.project_name) !== sessionFieldKey(b.project_name)) return false;
  if (sessionFieldKey(a.project_type) !== sessionFieldKey(b.project_type)) return false;
  if (sessionFieldKey(a.reader_id) !== sessionFieldKey(b.reader_id)) return false;
  if (sessionFieldKey(a.reader_name) !== sessionFieldKey(b.reader_name)) return false;
  const aIds = (a.reader_ids ?? []).join(',');
  const bIds = (b.reader_ids ?? []).join(',');
  if (aIds !== bIds) return false;
  if (sessionFieldKey(a.soundengineer_id) !== sessionFieldKey(b.soundengineer_id)) return false;
  if (sessionFieldKey(a.soundengineer_name) !== sessionFieldKey(b.soundengineer_name)) return false;
  if (sessionFieldKey(a.soundengineer_color) !== sessionFieldKey(b.soundengineer_color)) return false;
  if (!!a.is_recorded !== !!b.is_recorded) return false;
  if (!!a.is_partial !== !!b.is_partial) return false;
  if (!!a.is_canceled !== !!b.is_canceled) return false;
  if (!!a.canceled_hidden_from_schedule !== !!b.canceled_hidden_from_schedule) return false;
  if (sessionFieldKey(a.canceled_note) !== sessionFieldKey(b.canceled_note)) return false;
  if (sessionFieldKey(a.session_type) !== sessionFieldKey(b.session_type)) return false;
  if (sessionFieldKey(a.session_subtype) !== sessionFieldKey(b.session_subtype)) return false;
  if (sessionFieldKey(a.title) !== sessionFieldKey(b.title)) return false;
  if (sessionFieldKey(a.content) !== sessionFieldKey(b.content)) return false;
  if (sessionFieldKey(a.font_size) !== sessionFieldKey(b.font_size)) return false;
  if (!!a.is_header_hidden !== !!b.is_header_hidden) return false;
  if (!!a.is_reader_hidden !== !!b.is_reader_hidden) return false;
  if (!!a.is_project_hidden !== !!b.is_project_hidden) return false;
  if (!!a.is_progress_hidden !== !!b.is_progress_hidden) return false;
  if (sessionFieldKey(a.second_project_font_size) !== sessionFieldKey(b.second_project_font_size)) {
    return false;
  }
  if (sessionFieldKey(a.coef) !== sessionFieldKey(b.coef)) return false;
  if (sessionFieldKey(a.stop_page) !== sessionFieldKey(b.stop_page)) return false;
  return true;
}

/**
 * Resolve how to apply a realtime session:updated payload against a cached row.
 *
 * Progress lives on project_readers and is fan-out via session:updated without
 * bumping sessions.updated_at on siblings. Those payloads must still refresh
 * denormalized progress fields.
 *
 * Returns null when the cache should stay unchanged.
 */
export function applyRealtimeSessionUpdate(cached: Session, incoming: Session): Session | null {
  const incomingTs = sessionUpdatedAtMs(incoming);
  const cachedTs = sessionUpdatedAtMs(cached);
  const progressChanged = hasSessionProgressFieldsChanged(cached, incoming);
  const scheduleEqual = sessionScheduleFieldsEqual(cached, incoming);

  if (incomingTs > 0 && cachedTs > 0 && incomingTs <= cachedTs) {
    if (!progressChanged) return null;
    return mergeSessionProgressFields(cached, incoming);
  }

  if (scheduleEqual && !progressChanged) return null;
  if (scheduleEqual && progressChanged) return mergeSessionProgressFields(cached, incoming);

  return incoming;
}
